import React from 'react'

import clsx from 'clsx'

import Grid from 'components/Grid'
import InfoCircleTooltip from 'components/InfoCircleTooltip'
import Link from 'components/Link'
import Rating from 'components/Rating'
import Typography from 'components/Typography'
import { EventContext, useEventContext } from 'context/EventContext'
import useViewport from 'hooks/useViewport'

import GetListBanner from '../GetListBanner'
import CtaActions from './components/CtaActions'
import Methodology from './components/Methodology'
import PricingDisplay from './components/PricingDisplay'
import ProductCard from './components/ProductCard'
import SortSelect from './components/SortSelect'

import classes from './Top10.module.scss'

/** @type {import("./types").default} */
const Top10 = ({ className, listings, category, facet, hasDemoForm, onShowPricingModal, handleSortSelect }) => {
  const { up } = useViewport()

  const { addEvent } = useEventContext({
    engagementComponent: 'link-to_full-list',
  })

  const handleSmoothScroll = evt => {
    evt.preventDefault()
    const targetId = 'other'
    const targetElement = document.getElementById(targetId)

    if (targetElement) {
      targetElement.scrollIntoView({
        behavior: 'smooth',
      })
    }
  }

  const isDesktop = up('lg')

  return (
    <Grid className={clsx('Top10', classes.root, className)}>
      <div className={clsx(classes.item, classes.header)}>
        {isDesktop ? (
          <>
            <div className={classes.info}>
              <div className={classes.product}>Product</div>
              <div className={classes.rated_for}>User rating</div>
              <div className={classes.pricing}>
                <InfoCircleTooltip
                  className={classes.infoCircle}
                  label="Starting price"
                  title="Prices may vary depending on the number of users and features."
                />
              </div>
            </div>
            <div className={classes.ctas}>
              <SortSelect onSelect={handleSortSelect} />
            </div>
          </>
        ) : (
          <div className={classes.headers}>
            <div className={classes.product}>Product</div>
            <div className={classes.share}>
              <SortSelect onSelect={handleSortSelect} />
            </div>
          </div>
        )}
      </div>
      {listings.slice(0, 10).map((listing, index) => (
        <div className={classes.item} key={listing.id}>
          <div className={classes.info}>
            <div className={classes.product}>
              <ProductCard listing={listing} />
            </div>
            {isDesktop && (
              <div className={classes.rated_for}>
                <EventContext value={{ engagementComponent: 'best-products_list' }}>
                  <Rating.Link
                    isCompact
                    average={listing.rating_avg}
                    totals={listing.num_reviews}
                    href={listing.cta_urls.reviews}
                  />
                </EventContext>
              </div>
            )}
            {isDesktop && <PricingDisplay hasHeading={false} pricing={listing.pricing} />}
          </div>
          <div className={classes.ctas}>
            <CtaActions
              listing={listing}
              hasDemoForm={hasDemoForm}
              onShowPricingModal={onShowPricingModal}
              listingPosition={index}
            />
          </div>
        </div>
      ))}
      <div className={clsx(classes.item, classes.footer)}>
        <Typography>
          See other top{' '}
          <Link
            component="span"
            disableSeo={false}
            onClick={handleSmoothScroll}
            {...addEvent({
              engagementDetails: 'textlink_go-to_full-list',
              engagementDestination: 'go-to_full-list',
              name: 'engagement_navigation',
            })}
          >
            {category.name} products with {facet.label.toLowerCase()}
          </Link>
        </Typography>
      </div>

      <GetListBanner />
      <Methodology facet={facet.label.toLowerCase()} category={category.name} listings={listings} />
    </Grid>
  )
}

export default Top10
